import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  type ClipboardEvent,
  type ClipboardEventHandler,
  type FocusEvent,
  type FocusEventHandler,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';
import { useCharLimit, type CharLimit } from './CharCounter';
import { MarksToolbar, useMarks } from './MarksToolbar';

/**
 * DESIGN §5 Inputs: Canvas Sand fill, 0.5px hairline, 6px radius, 12–14px padding.
 * Persistent MONO UPPERCASE label above the field. Required = asterisk + aria-required.
 * Error = signal-error border + message below + aria-describedby.
 */
const inputBase =
  'w-full rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-[14px] py-[12px] ' +
  'font-sans text-small text-ink-graphite placeholder:text-ink-mute ' +
  'transition-[border-color] duration-almanac ease-almanac ' +
  'focus:border-accent-blue focus-visible:shadow-focus focus:outline-hidden ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

export interface FieldShellProps {
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  error?: string | null;
  hint?: ReactNode;
  /** Right-aligned adornment beside the label (e.g. completeness chip). */
  labelAdornment?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FieldShell({
  label,
  htmlFor,
  required,
  error,
  hint,
  labelAdornment,
  children,
  className,
}: FieldShellProps) {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  return (
    <div className={cn('flex flex-col gap-[6px]', className)}>
      {label && (
        <div className="flex items-center justify-between gap-sm">
          <label id={htmlFor ? `${htmlFor}-label` : undefined} htmlFor={htmlFor} className="eyebrow text-ink-mute">
            {label}
            {required && (
              <span className="ms-[3px] text-signal-error" aria-hidden>
                *
              </span>
            )}
          </label>
          {labelAdornment}
        </div>
      )}
      {children}
      {/* `max-w-prose` is on the help and error lines, not on the field: an
          input stretches to its column, but a sentence explaining it must not —
          a hint spanning a 1100px editor column measures ~140 characters, and
          the eye loses the start of the next line. */}
      {hint && !error && <p className="max-w-prose text-small text-ink-mute">{hint}</p>}
      {error && (
        <p id={errorId} className="max-w-prose text-small text-signal-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  labelAdornment?: ReactNode;
  /** A schema `maxLength`: a hard stop and a live "N left" counter (D-153). */
  charLimit?: number;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, required, labelAdornment, charLimit, id, className, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const limit = useCharLimit(String(rest.value ?? ''), charLimit);
  return (
    <FieldShell
      label={label}
      htmlFor={fieldId}
      required={required}
      error={error}
      hint={hint}
      labelAdornment={labelAdornment}
    >
      <input
        ref={ref}
        id={fieldId}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={cn(inputBase, error && 'border-signal-error', className)}
        {...rest}
        {...limitProps(limit, error ? `${fieldId}-error` : undefined, rest)}
      />
      {limit?.counter}
    </FieldShell>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  /** A schema `maxLength`: a hard stop and a live "N left" counter (D-153). */
  charLimit?: number;
  /**
   * The site renders this field's inline marks (`x-marks`, F-R16-04): a Bold / Italic /
   * Highlight toolbar over the textarea, and Ctrl/Cmd+B and +I inside it.
   */
  marks?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, required, charLimit, marks: withMarks = false, id, className, onKeyDown, onSelect, ...rest },
  ref,
) {
  const auto = useId();
  const fieldId = id ?? auto;
  const limit = useCharLimit(String(rest.value ?? ''), charLimit);
  const inner = useRef<HTMLTextAreaElement | null>(null);
  const setRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      inner.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  const marks = useMarks(inner, withMarks);
  return (
    <FieldShell label={label} htmlFor={fieldId} required={required} error={error} hint={hint}>
      {withMarks && <MarksToolbar controls={fieldId} marks={marks} disabled={rest.disabled || rest.readOnly} />}
      <textarea
        ref={setRef}
        id={fieldId}
        onKeyDown={(event) => {
          marks.onKeyDown(event);
          onKeyDown?.(event);
        }}
        onSelect={(event) => {
          marks.onSelect();
          onSelect?.(event);
        }}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={cn(inputBase, 'min-h-[96px] resize-y', error && 'border-signal-error', className)}
        {...rest}
        {...limitProps(limit, error ? `${fieldId}-error` : undefined, rest)}
      />
      {limit?.counter}
    </FieldShell>
  );
});

/**
 * The props a character limit adds to a control: the native stop, the paste
 * note, and the counter in `aria-describedby` beside any error message.
 */
function limitProps<E extends HTMLInputElement | HTMLTextAreaElement>(
  limit: CharLimit | null,
  errorId: string | undefined,
  rest: {
    onPaste?: ClipboardEventHandler<E>;
    onBlur?: FocusEventHandler<E>;
    'aria-describedby'?: string;
  },
) {
  const describedBy =
    [errorId, rest['aria-describedby'], limit?.describedBy].filter(Boolean).join(' ') || undefined;
  if (!limit) return { 'aria-describedby': describedBy };
  return {
    'aria-describedby': describedBy,
    maxLength: limit.inputProps.maxLength,
    onPaste: (e: ClipboardEvent<E>) => {
      limit.inputProps.onPaste(e);
      rest.onPaste?.(e);
    },
    onBlur: (e: FocusEvent<E>) => {
      limit.inputProps.onBlur();
      rest.onBlur?.(e);
    },
  };
}

export { inputBase, limitProps };
